import { z } from "zod";
import { companyRegistryShape } from "./crm.js";

/**
 * Import intelligente da Excel/CSV per Contatti e Aziende.
 *
 * Questo file è l'unica fonte di verità per:
 *  - i campi Kairos importabili (registry) e i loro alias di intestazione;
 *  - l'algoritmo di auto-mappatura colonna → campo (usato dal wizard web);
 *  - la normalizzazione dei valori di cella (sì/no, telefoni, date, importi);
 *  - gli schemi Zod del payload bulk validati dall'API.
 *
 * I formati riconosciuti automaticamente vivono in file dedicati accanto a
 * questo: vedi `import-atoka.ts`.
 */

// ── registry dei campi ───────────────────────────────────────────────

export type ImportFieldType =
  | "text"
  | "email"
  | "phone"
  | "boolean"
  | "country"
  | "companyName"
  | "fullName"
  | "integer"
  | "money"
  | "date";

export interface ImportFieldDef {
  /** chiave inviata all'API (o pseudo-campo gestito dal client, es. fullName) */
  key: string;
  /** chiave i18n: `importFields.<key>` */
  labelKey: string;
  type: ImportFieldType;
  /** obbligatorio: senza mappatura l'import non può partire */
  required?: boolean;
  /** almeno uno dei campi "identificativi" deve essere valorizzato nella riga */
  identifying?: boolean;
  /** usato per il rilevamento duplicati */
  dedupe?: boolean;
  aliases: readonly string[];
}

const ADDRESS_FIELDS = [
  {
    key: "address",
    labelKey: "importFields.address",
    type: "text",
    aliases: [
      "indirizzo", "via", "indirizzo 1", "indirizzo sede", "sede", "street",
      "address", "address line 1", "street address", "indirizzo completo",
    ],
  },
  {
    key: "city",
    labelKey: "importFields.city",
    type: "text",
    aliases: ["citta", "città", "comune", "city", "localita", "località", "town", "sede citta"],
  },
  {
    key: "zip",
    labelKey: "importFields.zip",
    type: "text",
    aliases: ["cap", "codice postale", "zip", "zip code", "postal code", "postcode"],
  },
  {
    key: "province",
    labelKey: "importFields.province",
    type: "text",
    aliases: ["provincia", "prov", "sigla provincia", "province", "state", "county"],
  },
  {
    key: "country",
    labelKey: "importFields.country",
    type: "country",
    aliases: ["paese", "nazione", "stato", "country", "nation", "country code"],
  },
] as const satisfies readonly ImportFieldDef[];

export const CONTACT_IMPORT_FIELDS: readonly ImportFieldDef[] = [
  {
    key: "firstName",
    labelKey: "importFields.firstName",
    type: "text",
    identifying: true,
    aliases: [
      "nome", "first name", "firstname", "given name", "nome contatto",
      "nome referente", "name", "forename",
    ],
  },
  {
    key: "lastName",
    labelKey: "importFields.lastName",
    type: "text",
    identifying: true,
    aliases: [
      "cognome", "last name", "lastname", "surname", "family name",
      "cognome contatto", "cognome referente",
    ],
  },
  {
    key: "fullName",
    labelKey: "importFields.fullName",
    type: "fullName",
    identifying: true,
    aliases: [
      "nome completo", "nome e cognome", "nominativo", "contatto", "referente",
      "full name", "display name", "name surname", "persona", "intestatario",
    ],
  },
  {
    key: "email",
    labelKey: "importFields.email",
    type: "email",
    identifying: true,
    dedupe: true,
    aliases: [
      "email", "e mail", "mail", "indirizzo email", "email address",
      "posta elettronica", "email principale", "email aziendale", "e mail address",
    ],
  },
  {
    key: "phone",
    labelKey: "importFields.phone",
    type: "phone",
    aliases: [
      "telefono", "tel", "phone", "telephone", "numero di telefono", "num telefono",
      "fisso", "centralino", "phone number", "telefono fisso", "telefono ufficio",
    ],
  },
  {
    key: "mobile",
    labelKey: "importFields.mobile",
    type: "phone",
    aliases: [
      "cellulare", "cell", "mobile", "mobile phone", "telefono cellulare",
      "cellulare aziendale", "gsm", "mobile number", "smartphone",
    ],
  },
  {
    key: "jobTitle",
    labelKey: "importFields.jobTitle",
    type: "text",
    aliases: [
      "ruolo", "qualifica", "posizione", "mansione", "job title", "title",
      "position", "role", "carica", "funzione",
    ],
  },
  {
    key: "companyName",
    labelKey: "importFields.companyName",
    type: "companyName",
    identifying: true,
    aliases: [
      "azienda", "societa", "società", "ragione sociale", "company", "company name",
      "organizzazione", "organization", "ditta", "cliente", "account", "denominazione",
      "nome azienda", "employer",
    ],
  },
  ...ADDRESS_FIELDS,
  {
    key: "marketingConsent",
    labelKey: "importFields.marketingConsent",
    type: "boolean",
    aliases: [
      "consenso marketing", "consenso", "marketing", "marketing consent", "newsletter",
      "opt in", "optin", "privacy", "gdpr", "consenso newsletter", "iscritto newsletter",
      "subscribed",
    ],
  },
];

/**
 * Dati di registro imprese: presenti negli export tipo Atoka, Cerved,
 * visure camerali. Mappabili anche a mano da qualsiasi file.
 */
export const COMPANY_REGISTRY_IMPORT_FIELDS = [
  {
    key: "fiscalCode",
    labelKey: "importFields.fiscalCode",
    type: "text",
    dedupe: true,
    aliases: [
      "codice fiscale", "cod fiscale", "cf", "c f", "codice fiscale azienda",
      "tax code", "fiscal code",
    ],
  },
  {
    key: "reaNumber",
    labelKey: "importFields.reaNumber",
    type: "text",
    aliases: ["numero rea", "rea", "n rea", "iscrizione rea", "numero repertorio economico"],
  },
  {
    key: "reaProvince",
    labelKey: "importFields.reaProvince",
    type: "text",
    aliases: ["provincia rea", "cciaa", "camera di commercio"],
  },
  {
    key: "legalForm",
    labelKey: "importFields.legalForm",
    type: "text",
    aliases: ["natura giuridica", "forma giuridica", "tipo societa", "legal form"],
  },
  {
    key: "foundedOn",
    labelKey: "importFields.foundedOn",
    type: "date",
    aliases: [
      "sul mercato da", "data costituzione", "data di costituzione", "anno fondazione",
      "founded", "founded on", "data iscrizione",
    ],
  },
  {
    key: "atecoCode",
    labelKey: "importFields.atecoCode",
    type: "text",
    aliases: ["codice ateco 2007", "codice ateco", "ateco 2007", "cod ateco"],
  },
  {
    key: "atecoDescription",
    labelKey: "importFields.atecoDescription",
    type: "text",
    aliases: ["descrizione ateco 2007", "descrizione ateco", "attivita ateco"],
  },
  {
    key: "naceCode",
    labelKey: "importFields.naceCode",
    type: "text",
    aliases: ["codice nace", "nace", "nace code"],
  },
  {
    key: "naceDescription",
    labelKey: "importFields.naceDescription",
    type: "text",
    aliases: ["descrizione nace", "nace description"],
  },
  {
    key: "region",
    labelKey: "importFields.region",
    type: "text",
    aliases: ["regione", "regione sede legale", "region"],
  },
  {
    key: "geoArea",
    labelKey: "importFields.geoArea",
    type: "text",
    aliases: ["area geografica", "area geografica sede legale", "macroarea", "ripartizione"],
  },
  {
    key: "employees",
    labelKey: "importFields.employees",
    type: "integer",
    aliases: [
      "numero dipendenti", "dipendenti", "n dipendenti", "addetti", "employees",
      "numero addetti", "headcount",
    ],
  },
  {
    key: "revenueCents",
    labelKey: "importFields.revenue",
    type: "money",
    aliases: [
      "ricavi", "ricavi euro", "fatturato", "revenue", "turnover", "volume d affari",
      "ricavi delle vendite",
    ],
  },
  {
    key: "balanceDate",
    labelKey: "importFields.balanceDate",
    type: "date",
    aliases: [
      "data chiusura bilancio", "chiusura bilancio", "data bilancio", "anno bilancio",
      "balance date",
    ],
  },
  {
    key: "localUnits",
    labelKey: "importFields.localUnits",
    type: "integer",
    aliases: [
      "numero unita locali oltre alla sede legale", "numero unita locali", "unita locali",
      "sedi secondarie", "local units",
    ],
  },
  {
    key: "isStartup",
    labelKey: "importFields.isStartup",
    type: "boolean",
    aliases: ["startup innovativa", "startup", "pmi innovativa"],
  },
  {
    key: "isOperational",
    labelKey: "importFields.isOperational",
    type: "boolean",
    aliases: ["operativa", "attiva", "stato attivita", "active"],
  },
  {
    key: "isListed",
    labelKey: "importFields.isListed",
    type: "boolean",
    aliases: ["quotata", "quotata in borsa", "listed"],
  },
  {
    key: "cms",
    labelKey: "importFields.cms",
    type: "text",
    aliases: ["cms", "piattaforma sito"],
  },
  {
    key: "ecommerce",
    labelKey: "importFields.ecommerce",
    type: "text",
    aliases: ["ecommerce", "e commerce", "piattaforma ecommerce"],
  },
  {
    key: "sourceUrl",
    labelKey: "importFields.sourceUrl",
    type: "text",
    aliases: ["url atoka", "url scheda", "link scheda", "source url", "profilo"],
  },
] as const satisfies readonly ImportFieldDef[];

export const COMPANY_IMPORT_FIELDS: readonly ImportFieldDef[] = [
  {
    key: "name",
    labelKey: "importFields.companyLegalName",
    type: "text",
    required: true,
    identifying: true,
    dedupe: true,
    aliases: [
      "ragione sociale", "azienda", "nome azienda", "denominazione", "company",
      "company name", "nome", "name", "ditta", "cliente", "organizzazione",
      "organization", "business name", "societa", "società", "account",
    ],
  },
  {
    key: "vatNumber",
    labelKey: "importFields.vatNumber",
    type: "text",
    dedupe: true,
    aliases: [
      "partita iva", "p iva", "piva", "partitaiva", "iva", "vat", "vat number",
      "vat id", "numero partita iva", "codice iva",
    ],
  },
  {
    key: "website",
    labelKey: "importFields.website",
    type: "text",
    aliases: [
      "sito", "sito web", "sito internet", "website", "web", "url", "homepage",
      "web site", "pagina web",
    ],
  },
  {
    key: "industry",
    labelKey: "importFields.industry",
    type: "text",
    aliases: [
      "settore", "industria", "categoria", "industry", "sector", "mercato",
      "attivita", "attività", "ateco", "segmento",
    ],
  },
  {
    key: "email",
    labelKey: "importFields.email",
    type: "email",
    aliases: [
      "email", "e mail", "mail", "indirizzo email", "email address",
      "posta elettronica", "email aziendale", "pec",
    ],
  },
  {
    key: "phone",
    labelKey: "importFields.phone",
    type: "phone",
    aliases: [
      "telefono", "tel", "phone", "telephone", "numero di telefono", "centralino",
      "phone number", "telefono aziendale",
    ],
  },
  ...ADDRESS_FIELDS,
  ...COMPANY_REGISTRY_IMPORT_FIELDS,
];

export function importFieldsFor(entity: "contacts" | "companies"): readonly ImportFieldDef[] {
  return entity === "contacts" ? CONTACT_IMPORT_FIELDS : COMPANY_IMPORT_FIELDS;
}

// ── auto-mappatura ───────────────────────────────────────────────────

/** minuscolo, senza accenti e senza punteggiatura: "P.IVA " → "p iva" */
export function normalizeHeader(raw: string): string {
  return raw
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const curr = [i];
    for (let j = 1; j <= b.length; j++) {
      curr[j] = Math.min(
        prev[j]! + 1,
        curr[j - 1]! + 1,
        prev[j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
    }
    prev = curr;
  }
  return prev[b.length]!;
}

function similarity(a: string, b: string): number {
  const max = Math.max(a.length, b.length);
  return max === 0 ? 0 : 1 - levenshtein(a, b) / max;
}

/**
 * Punteggio 0-100 di quanto un'intestazione di colonna somiglia a un campo.
 * Scala: match esatto 100 → contenimento ~60 → fuzzy ~50.
 */
export function scoreHeaderForField(header: string, field: ImportFieldDef): number {
  const h = normalizeHeader(header);
  if (!h) return 0;
  const hTight = h.replace(/ /g, "");
  let best = 0;
  for (const rawAlias of field.aliases) {
    const a = normalizeHeader(rawAlias);
    if (!a) continue;
    const aTight = a.replace(/ /g, "");
    let score = 0;
    if (h === a) score = 100;
    else if (hTight === aTight) score = 96;
    else if (h.startsWith(`${a} `) || h.endsWith(` ${a}`)) score = 84;
    else if (a.startsWith(`${h} `) || a.endsWith(` ${h}`)) score = 76;
    else if (aTight.length >= 4 && hTight.includes(aTight)) score = 66;
    else if (hTight.length >= 4 && aTight.includes(hTight)) score = 60;
    else {
      const sim = similarity(hTight, aTight);
      if (sim >= 0.82 && Math.min(hTight.length, aTight.length) >= 4) {
        score = Math.round(sim * 60);
      }
    }
    if (score > best) best = score;
  }
  return best;
}

export interface MappingSuggestion {
  columnIndex: number;
  fieldKey: string;
  /** 0-100: mostrato nel wizard come livello di confidenza */
  score: number;
}

/**
 * Assegna al massimo un campo per colonna e una colonna per campo,
 * scegliendo greedy dai punteggi più alti.
 */
export function suggestMapping(
  headers: readonly string[],
  fields: readonly ImportFieldDef[],
  threshold = 55,
): MappingSuggestion[] {
  const candidates: MappingSuggestion[] = [];
  headers.forEach((header, columnIndex) => {
    for (const field of fields) {
      const score = scoreHeaderForField(header, field);
      if (score >= threshold) candidates.push({ columnIndex, fieldKey: field.key, score });
    }
  });
  candidates.sort((a, b) =>
    b.score !== a.score ? b.score - a.score : a.columnIndex - b.columnIndex,
  );

  const usedColumns = new Set<number>();
  const usedFields = new Set<string>();
  const chosen: MappingSuggestion[] = [];
  for (const c of candidates) {
    if (usedColumns.has(c.columnIndex) || usedFields.has(c.fieldKey)) continue;
    // nome+cognome separati battono sempre il campo unico "nome completo"
    if (c.fieldKey === "fullName" && usedFields.has("firstName")) continue;
    if (c.fieldKey === "firstName" && usedFields.has("fullName")) {
      const full = chosen.find((x) => x.fieldKey === "fullName");
      if (full && c.score > full.score) {
        usedFields.delete("fullName");
        usedColumns.delete(full.columnIndex);
        chosen.splice(chosen.indexOf(full), 1);
      } else continue;
    }
    usedColumns.add(c.columnIndex);
    usedFields.add(c.fieldKey);
    chosen.push(c);
  }
  return chosen.sort((a, b) => a.columnIndex - b.columnIndex);
}

// ── normalizzazione dei valori di cella ──────────────────────────────

const TRUTHY = new Set([
  "si", "sì", "s", "yes", "y", "true", "vero", "1", "x", "ok",
  "attivo", "attiva", "iscritto", "consenso", "acconsente", "subscribed", "opt in", "opt-in",
]);
const FALSY = new Set([
  "no", "n", "false", "falso", "0", "-", "non iscritto", "unsubscribed", "opt out", "opt-out",
  "cessata", "inattiva",
]);

export function parseImportBoolean(value: string): boolean | null {
  const v = value.trim().toLowerCase();
  if (!v) return null;
  if (TRUTHY.has(v)) return true;
  if (FALSY.has(v)) return false;
  return null;
}

/**
 * Excel trasforma spesso i telefoni in numeri: "3.93331E+11" o "3933312345.0".
 * Qui si torna a una stringa leggibile.
 */
export function cleanImportPhone(value: string): string {
  const v = value.trim();
  if (!v) return "";
  if (/^[+-]?\d+(\.\d+)?[eE][+-]?\d+$/.test(v)) {
    const n = Number(v);
    if (Number.isFinite(n)) return n.toFixed(0);
  }
  return v.replace(/\.0+$/, "");
}

export const IMPORT_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Alcuni export incollano più indirizzi separati da ; o , : si tiene il primo. */
export function cleanImportEmail(value: string): string {
  return value.trim().toLowerCase().split(/[;,\s]+/)[0] ?? "";
}

/** "Mario Rossi" → { firstName: "Mario", lastName: "Rossi" }; "Rossi, Mario" → invertito. */
export function splitPersonName(value: string): { firstName: string; lastName: string } {
  const v = value.trim().replace(/\s+/g, " ");
  if (!v) return { firstName: "", lastName: "" };
  if (v.includes(",")) {
    const [last = "", first = ""] = v.split(",", 2).map((s) => s.trim());
    return { firstName: first || last, lastName: first ? last : "" };
  }
  const parts = v.split(" ");
  if (parts.length === 1) return { firstName: parts[0]!, lastName: "" };
  return { firstName: parts[0]!, lastName: parts.slice(1).join(" ") };
}

/** "8.097" / "8097,0" / "8097" → 8097. Restituisce null se non è un numero. */
export function parseImportInteger(value: string): number | null {
  const n = parseImportNumber(value);
  return n == null ? null : Math.round(n);
}

/** "1.015.294.000,50" o "1015294000.5" → 101529400050 centesimi. */
export function parseImportMoneyCents(value: string): number | null {
  const n = parseImportNumber(value);
  return n == null ? null : Math.round(n * 100);
}

/**
 * Numero scritto all'italiana o all'inglese. La regola: l'ultimo separatore
 * seguito da 1-2 cifre è decimale, tutti gli altri sono migliaia.
 */
export function parseImportNumber(value: string): number | null {
  let v = value.trim().replace(/[€$£\s ]/g, "");
  if (!v) return null;
  const negative = /^\(.*\)$/.test(v) || v.startsWith("-");
  v = v.replace(/^[-(]|\)$/g, "");
  if (!/^[\d.,]+$/.test(v)) return null;

  const lastComma = v.lastIndexOf(",");
  const lastDot = v.lastIndexOf(".");
  const sep = Math.max(lastComma, lastDot);
  let decimals = "";
  let units = v;
  if (sep >= 0 && v.length - sep - 1 <= 2 && v.length - sep - 1 > 0) {
    // ambiguo solo se il separatore compare una volta sola: "1.015" resta intero
    const separator = v[sep]!;
    const occurrences = v.split(separator).length - 1;
    const isDecimal = separator === "," || occurrences === 1;
    if (isDecimal) {
      decimals = v.slice(sep + 1);
      units = v.slice(0, sep);
    }
  }
  units = units.replace(/[.,]/g, "");
  if (!units && !decimals) return null;
  const n = Number(`${units || "0"}.${decimals || "0"}`);
  if (!Number.isFinite(n)) return null;
  return negative ? -n : n;
}

/** Qualsiasi data leggibile → "AAAA-MM-GG". Null se non riconosciuta. */
export function parseImportDate(value: string): string | null {
  const v = value.trim();
  if (!v) return null;

  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
  if (iso) return validDay(Number(iso[1]), Number(iso[2]), Number(iso[3]));

  // 27/04/1961, 27-4-1961, 27.04.1961 (giorno per primo: formato italiano)
  const dmy = /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})/.exec(v);
  if (dmy) return validDay(Number(dmy[3]), Number(dmy[2]), Number(dmy[1]));

  // solo anno: "2019"
  if (/^\d{4}$/.test(v)) return validDay(Number(v), 1, 1);

  const parsed = new Date(v);
  if (!Number.isNaN(parsed.getTime())) {
    return validDay(parsed.getUTCFullYear(), parsed.getUTCMonth() + 1, parsed.getUTCDate());
  }
  return null;
}

function validDay(year: number, month: number, day: number): string | null {
  if (year < 1800 || year > 2200 || month < 1 || month > 12 || day < 1 || day > 31) return null;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${year}-${pad(month)}-${pad(day)}`;
}

/** Converte il testo della cella nel tipo atteso dal campo Kairos. */
export function coerceImportValue(
  type: ImportFieldType,
  raw: string,
): string | number | boolean | null {
  const v = raw.trim();
  if (!v) return null;
  switch (type) {
    case "boolean":
      return parseImportBoolean(v);
    case "integer":
      return parseImportInteger(v);
    case "money":
      return parseImportMoneyCents(v);
    case "date":
      return parseImportDate(v);
    case "phone":
      return cleanImportPhone(v) || null;
    case "email":
      return cleanImportEmail(v) || null;
    default:
      return v;
  }
}

// ── payload bulk (API) ───────────────────────────────────────────────

export const IMPORT_DUPLICATE_STRATEGIES = ["skip", "update", "create"] as const;
export type ImportDuplicateStrategy = (typeof IMPORT_DUPLICATE_STRATEGIES)[number];

/** righe per singola richiesta: il client spezza il file in lotti */
export const IMPORT_BATCH_SIZE = 400;

const cell = z
  .string()
  .trim()
  .max(500)
  .optional()
  .nullable()
  .transform((v) => (v === "" ? null : (v ?? null)));

const customFields = z
  .record(z.string().trim().min(1).max(120), z.string().trim().max(1000))
  .optional()
  .default({})
  .refine((v) => Object.keys(v).length <= 40, {
    message: "Troppi campi personalizzati (max 40)",
  });

const baseRow = {
  /** indice della riga nel file originale (1-based, intestazione esclusa) */
  rowNumber: z.coerce.number().int().min(1),
  email: cell,
  phone: cell,
  address: cell,
  city: cell,
  zip: cell,
  province: cell,
  country: cell,
  custom: customFields,
};

export const contactImportRowSchema = z.object({
  ...baseRow,
  firstName: cell,
  lastName: cell,
  mobile: cell,
  jobTitle: cell,
  companyName: cell,
  marketingConsent: z.boolean().optional().nullable(),
});
export type ContactImportRow = z.infer<typeof contactImportRowSchema>;

/**
 * Persona collegata all'azienda nella stessa riga del file (gli "esponenti"
 * di Atoka). Diventa un contatto agganciato all'azienda importata.
 */
export const importPersonSchema = z.object({
  firstName: cell,
  lastName: cell,
  jobTitle: cell,
  email: cell,
  phone: cell,
  mobile: cell,
});
export type ImportPerson = z.infer<typeof importPersonSchema>;

/** massimo di persone per riga: gli export ne portano 3-5 */
export const IMPORT_MAX_PEOPLE_PER_ROW = 10;

export const companyImportRowSchema = z.object({
  ...baseRow,
  name: cell,
  vatNumber: cell,
  website: cell,
  industry: cell,
  ...companyRegistryShape,
  people: z.array(importPersonSchema).max(IMPORT_MAX_PEOPLE_PER_ROW).optional().default([]),
});
export type CompanyImportRow = z.infer<typeof companyImportRowSchema>;

export const contactImportSchema = z.object({
  rows: z.array(contactImportRowSchema).min(1).max(IMPORT_BATCH_SIZE),
  duplicateStrategy: z.enum(IMPORT_DUPLICATE_STRATEGIES).default("skip"),
  /** l'azienda indicata per nome viene creata se non esiste */
  createMissingCompanies: z.boolean().default(true),
  /** esegue le automazioni "contatto creato" (sconsigliato su import massivi) */
  runAutomations: z.boolean().default(false),
});
export type ContactImportInput = z.infer<typeof contactImportSchema>;

export const companyImportSchema = z.object({
  rows: z.array(companyImportRowSchema).min(1).max(IMPORT_BATCH_SIZE),
  duplicateStrategy: z.enum(IMPORT_DUPLICATE_STRATEGIES).default("skip"),
  /** crea i contatti indicati in `people` e li collega all'azienda della riga */
  importPeople: z.boolean().default(true),
});
export type CompanyImportInput = z.infer<typeof companyImportSchema>;

export interface ImportRowError {
  rowNumber: number;
  code: "empty_row" | "missing_required" | "invalid_email" | "plan_limit" | "error";
  message: string;
}

export interface ImportResult {
  created: number;
  updated: number;
  skipped: number;
  failed: number;
  /** aziende create automaticamente durante l'import dei contatti */
  companiesCreated: number;
  /** contatti creati dalle persone collegate durante l'import delle aziende */
  contactsCreated: number;
  /** contatti già presenti e arricchiti durante l'import delle aziende */
  contactsUpdated: number;
  errors: ImportRowError[];
  /** valorizzato quando il limite di contatti del piano blocca l'import */
  planLimit: { limit: number; current: number } | null;
  /**
   * Limite contatti raggiunto durante l'import delle *aziende*: le persone
   * collegate non vengono create, ma le aziende sì. Non interrompe il lotto.
   */
  contactsPlanLimit: { limit: number; current: number } | null;
}

export function emptyImportResult(): ImportResult {
  return {
    created: 0,
    updated: 0,
    skipped: 0,
    failed: 0,
    companiesCreated: 0,
    contactsCreated: 0,
    contactsUpdated: 0,
    errors: [],
    planLimit: null,
    contactsPlanLimit: null,
  };
}
